const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const monthKey = (iso) => (iso || "").slice(0, 7);

const label = (key) => {
  const [y, m] = key.split("-");
  return `${MONTHS[Number(m) - 1]} ${y.slice(2)}`;
};

// Last six months, oldest first
const lastSixMonths = () => {
  const now = new Date();
  return Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
};

export const OwnerEnquiryStats = ({ enquiries }) => {
  const months = lastSixMonths();
  const counts = months.map((k) => enquiries.filter((e) => monthKey(e.created_at) === k).length);
  const peak = Math.max(...counts, 1);
  const thisMonth = counts[5];
  const lastMonth = counts[4];

  const byProperty = Object.values(
    enquiries.reduce((acc, e) => {
      const slug = e.property_slug || "general";
      const name = e.property_name || "General enquiry";
      acc[slug] = acc[slug] || { slug, name, total: 0, thisMonth: 0, lastMonth: 0 };
      acc[slug].total += 1;
      if (monthKey(e.created_at) === months[5]) acc[slug].thisMonth += 1;
      if (monthKey(e.created_at) === months[4]) acc[slug].lastMonth += 1;
      return acc;
    }, {})
  ).sort((a, b) => b.total - a.total);

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-md p-5" data-testid="owner-enquiry-stats">
      <div className="flex flex-wrap gap-x-10 gap-y-3">
        <div>
          <p className="text-xs uppercase tracking-wider text-neutral-500">This month</p>
          <p className="text-2xl text-[#0F2A52] mt-1" data-testid="stats-this-month">
            {thisMonth}
          </p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-neutral-500">Last month</p>
          <p className="text-2xl text-[#0F2A52] mt-1" data-testid="stats-last-month">
            {lastMonth}
          </p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-neutral-500">All time</p>
          <p className="text-2xl text-[#0F2A52] mt-1" data-testid="stats-total">
            {enquiries.length}
          </p>
        </div>
      </div>

      <div className="flex items-end gap-3 mt-6 h-24" data-testid="stats-chart">
        {months.map((k, i) => (
          <div key={k} className="flex-1 flex flex-col items-center justify-end gap-2">
            <span className="text-xs text-[#0F2A52]">{counts[i]}</span>
            <div
              className={`w-full ${i === 5 ? "bg-[#B08A3E]" : "bg-[#0F2A52]/25"}`}
              style={{ height: `${Math.max((counts[i] / peak) * 62, 3)}px` }}
              data-testid={`stats-bar-${k}`}
            />
            <span className="text-[10px] uppercase tracking-wider text-neutral-500">{label(k)}</span>
          </div>
        ))}
      </div>

      <p className="text-xs text-neutral-500 mt-4">
        Direct enquiries received through the website. Deleted enquiries are not counted.
      </p>

      <div className="mt-6 pt-5 border-t border-[#E5E5E5]" data-testid="stats-by-property">
        <p className="text-xs uppercase tracking-wider text-neutral-500">Which home they asked about</p>
        <table className="w-full mt-3 text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-neutral-500">
              <th className="text-left font-normal pb-2">Property</th>
              <th className="text-right font-normal pb-2">This month</th>
              <th className="text-right font-normal pb-2">Last month</th>
              <th className="text-right font-normal pb-2">All time</th>
            </tr>
          </thead>
          <tbody>
            {byProperty.map((row) => (
              <tr key={row.name} className="border-t border-[#F0F0F0]" data-testid={`stats-row-${row.slug}`}>
                <td className="py-2 pr-2">{row.name}</td>
                <td className="py-2 text-right">{row.thisMonth}</td>
                <td className="py-2 text-right text-neutral-500">{row.lastMonth}</td>
                <td className="py-2 text-right font-medium text-[#0F2A52]">{row.total}</td>
              </tr>
            ))}
            {!byProperty.length && (
              <tr>
                <td className="py-3 text-neutral-400" colSpan={4}>
                  No enquiries yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OwnerEnquiryStats;
